import { createFeatureSelector, createSelector } from '@ngrx/store';

import { State } from '@app/stp/state/reducers';

import { selectJourneyPath } from '@app/stp/state/selectors/journey.selectors';

import { PremiumHelpers } from '@app/infrastructure/helpers/premium-helpers';

import { ChangeTypeEnum } from '@global/infrastructure/enums/change-type.enum';
import { TransactionTypeEnum } from '@app/infrastructure/enums/transaction-type.enum';


const selectStpStore = createFeatureSelector<State>('stpStore');
const selectStpState = createSelector(
  selectStpStore,
  state => state.stpState
);

const selectPolicyState = createSelector(
  selectStpStore,
  (state: State) => state?.policyState
);

const selectInitialPolicyDetails = createSelector(
  selectStpStore,
  (state: State) => state.policyState.initialState.policyDetails.result
);

export const selectPolicyId = createSelector(
  selectStpStore,
  (state: State) => {
    if (state) {
      const { policyNumber } =
      state?.policyState?.initialState?.policyDetails?.result ?? { policyNumber: ''};
      return policyNumber || '';
    }
    return '';
  }
);

export const getVehicleDetails = createSelector(
  selectStpStore,
  (state: State) => {
    if (state) {
      const policy = state?.policyState?.initialState?.policyDetails;
      const vehicle = (policy?.result?.lobData?.mOTLine_Ext?.coverables
        ?.motVehicles ?? [])[0]?.vehicle;
      return {
        make: `${vehicle?.make} ${vehicle?.model}`,
        reg: vehicle?.registrationNumber,
      };
    }
    return { make: '', reg: '' };
  }
);

export const getEffectiveDate = createSelector(
  selectInitialPolicyDetails,
  ({baseData: {effectiveDate}}) => effectiveDate
);

export const isAutoRenewed = createSelector(
  selectInitialPolicyDetails,
  ({autoRenewInd_dlg}) => autoRenewInd_dlg
);

export const getExpirationDate = createSelector(
  selectInitialPolicyDetails,
  ({baseData: {expirationDate_dlg}}) => expirationDate_dlg
);


export const getTransactionType = createSelector(
  selectInitialPolicyDetails,
  ({baseData: {transactionType_Dlg}}) => transactionType_Dlg.toLowerCase() as TransactionTypeEnum
);

export const getLatestTempChangeEndDate = createSelector(
  selectInitialPolicyDetails,
  premium => PremiumHelpers.extractLatestTempChangeEndDate(premium)
);

export const getPeriodEndDate = createSelector(
  selectInitialPolicyDetails,
  ({baseData: {periodEnd}}) => periodEnd
);

export const getPeriodStartDate = createSelector(
  selectInitialPolicyDetails,
  ({baseData: {periodStart}}) => periodStart
);

export const selectPolicyChangeDate = createSelector(
  selectStpState,
  state => state.policyChangeDate
);

export const selectProgressbarDisplayStatus = createSelector(
  selectStpState,
  state => state.isDisplayProgressBar
);

export const selectPolicyChangeSelection = createSelector(
  selectStpState,
  state => state.policyChangeSelection
);

export const selectPolicyChangeSelectionForConfirm = createSelector(
  selectStpState,
  state => state.isQuoteRetrieved
    ? {
      ...state.policyChangeSelection,
      options: state.policyChangeSelection.changeType === ChangeTypeEnum.Permanent
        ? {...state.policyChangeSelection.options, addOns: true}
        : state.policyChangeSelection.options
    } : state.policyChangeSelection
);

export const selectPolicyQuoteRetrievedStatus = createSelector(
  selectStpState,
  state => state.isQuoteRetrieved
);

export const selectPolicyQuoteRetrievedStatusAndChangeType = createSelector(
  selectStpState,
  state => {
    const {isQuoteRetrieved, policyChangeSelection: {changeType}} = state;
    return {isQuoteRetrieved, changeType};
  }
);

export const selectAdditionalDriversBanDates = createSelector(
  selectStpState,
  state => state.banDates ?
    [...new Set(
      state.banDates
        .map(b => b.value)
        .reduce((acc, current) => [...acc, ...current], []))
    ] : []
);

export const selectQuotedStatus = createSelector(
  selectStpState,
  state => state.isQuoted
);

export const selectTempDuration = createSelector(
  selectStpState,
  state => {
    const {policyChangeDate, tempChangeDuration: {startDate, endDate}} = state;
    return {
      startDate: startDate || policyChangeDate,
      endDate
    };
  }
);

export const selectNavigateToMyAccountStatus = (featureName: string) => createSelector(
  selectPolicyQuoteRetrievedStatus,
  selectJourneyPath,
  (isQuoteRetrieved, journeyPath) => isQuoteRetrieved && journeyPath.path.indexOf(featureName) === 0
);

export const selectYourBillingSummary = createSelector(
  selectPolicyState,
  (state) => {
    const bindData = state?.requestPayload.premium.bindData;
    const paymentPlan = bindData.selectedPaymentPlan;
    if (bindData && paymentPlan) {
      if (paymentPlan === 'dlg_payment_plan:1') {
        return {
          billingAddres: bindData.creditCardDetails_dlg.billingAddres,
          isPolicyHolderPayerInd: bindData.creditCardDetails_dlg.isPolicyHolderPayerInd
        };
      } else {
        return {
          billingAddres: bindData.directDebitDetails_dlg.billingAddres,
          isPolicyHolderPayerInd: bindData.directDebitDetails_dlg.isPolicyHolderPayerInd
        };
      }
    }
    return null;
  }
);

export const selectPostalAddressStatus = createSelector(
  selectStpState,
  state => state.isYourPostalAdressSelected
);

export const selectPolicyAddressStatus = createSelector(
  selectStpState,
  state => state.isYourPolicyAdressSelected
);
